import { z } from 'zod';
import {
  dataHoraSchema,
  listagemDeCadastroSchema,
  respostaListaSchema,
  sentidoMovimentacaoEstoqueSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Tipo de movimentação de estoque (`.spec/010`, Movimentação de estoque).
 *
 * É o cadastro que **nomeia** o porquê de o material entrar ou sair: "entrada
 * por nota", "devolução de obra", "perda", "consumo". Toda linha de
 * movimentação aponta para um deles, e é por esse nome que o relatório de
 * estoque se lê — sem ele, o histórico do depósito seria uma coluna de
 * quantidades com sinal e nenhuma explicação.
 *
 * O cadastro **não movimenta nada**. Ele classifica: quem soma e subtrai é a
 * movimentação de estoque, que lê o sentido daqui para saber para que lado
 * andar.
 */

/* -------------------------------------------------------------------------- */
/* Sentido                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Entrada ou saída.
 *
 * O `z.enum` mora em `comum.ts`, junto com os outros códigos estáveis do
 * domínio, e aqui só se derivam a lista e o tipo. Redeclará-lo daria dois
 * lugares para acrescentar um código novo — e o esquecido seria justamente o
 * que a coluna `CHECK tipos_movimentacao_estoque_sentido_valido` valida.
 */
export const SENTIDOS_MOVIMENTACAO_ESTOQUE = sentidoMovimentacaoEstoqueSchema.options;
export type SentidoMovimentacaoEstoque = z.infer<typeof sentidoMovimentacaoEstoqueSchema>;

/** Rótulos de tela. Ficam no contrato para API e web nunca escreverem dois nomes. */
export const ROTULO_SENTIDO_MOVIMENTACAO_ESTOQUE: Readonly<
  Record<SentidoMovimentacaoEstoque, string>
> = {
  ENTRADA: 'Entrada',
  SAIDA: 'Saída',
};

/**
 * O que cada sentido faz com o saldo, para a dica do formulário.
 *
 * "Entrada" e "saída" parecem dispensar explicação, e não dispensam: o que muda
 * entre os dois não é só o sinal da quantidade. A saída retira o valor pelo
 * custo médio vigente naquele depósito, e é isso que decide se a caixa
 * "atualiza custo" faz sentido.
 */
export const DICA_SENTIDO_MOVIMENTACAO_ESTOQUE: Readonly<
  Record<SentidoMovimentacaoEstoque, string>
> = {
  ENTRADA: 'Soma quantidade e valor ao saldo do depósito. É o sentido da nota e da devolução.',
  SAIDA:
    'Retira a quantidade e baixa o valor pelo custo médio vigente no depósito. É o sentido do consumo e da perda.',
};

/**
 * Só entrada atualiza custo.
 *
 * A saída baixa o estoque pelo custo médio que **já existe** — ela consome o
 * custo, não o forma (`.spec/010`, Movimentação de estoque). Marcar a caixa num
 * tipo de saída seria promessa que nenhum cálculo cumpre: o usuário veria a
 * opção ligada e o custo médio continuaria intacto depois do lançamento, sem
 * erro nenhum aparecendo.
 *
 * A regra vive em três camadas de propósito — aqui, no service e no
 * `CHECK tipos_movimentacao_estoque_custo_so_entrada`. É a mesma escolha feita
 * para o NCM que só vale em insumo material: a tela explica, a API decide, e o
 * banco é a garantia final contra um `UPDATE` direto ou um script de importação.
 */
export function atualizaCustoCoerente(entrada: {
  sentido: SentidoMovimentacaoEstoque;
  atualizaCusto: boolean;
}): boolean {
  return !entrada.atualizaCusto || entrada.sentido === 'ENTRADA';
}

/* -------------------------------------------------------------------------- */
/* Cadastro                                                                   */
/* -------------------------------------------------------------------------- */

const nomeSchema = z
  .string()
  .trim()
  .min(2, 'Informe o nome do tipo de movimentação.')
  .max(200, 'O nome tem no máximo 200 caracteres.');

/**
 * Payload de gravação.
 *
 * O `superRefine` aponta o erro em `atualizaCusto`, e não em `sentido`: quem
 * está errado é a caixa marcada, e o sentido é a decisão que o usuário já tomou
 * — apontar para ele mandaria corrigir o campo certo.
 */
export const criarTipoMovimentacaoEstoqueSchema = z
  .object({
    nome: nomeSchema,
    sentido: sentidoMovimentacaoEstoqueSchema,
    /**
     * O tipo forma o custo médio do insumo no depósito.
     *
     * Nasce **desmarcado**: atualizar custo é a exceção — vale para a entrada
     * por nota e pouco mais —, e um padrão ligado faria toda devolução
     * cadastrada às pressas reescrever o custo médio da obra.
     */
    atualizaCusto: z.boolean().prefault(false),
    ativo: z.boolean().prefault(true),
  })
  .superRefine((dados, ctx) => {
    if (atualizaCustoCoerente(dados)) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['atualizaCusto'],
      message: 'Só entrada atualiza custo: a saída baixa o estoque pelo custo médio que já existe.',
    });
  });

export type CriarTipoMovimentacaoEstoque = z.infer<typeof criarTipoMovimentacaoEstoqueSchema>;
export const atualizarTipoMovimentacaoEstoqueSchema = criarTipoMovimentacaoEstoqueSchema;
export type AtualizarTipoMovimentacaoEstoque = z.infer<
  typeof atualizarTipoMovimentacaoEstoqueSchema
>;

export const tipoMovimentacaoEstoqueSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  sentido: sentidoMovimentacaoEstoqueSchema,
  atualizaCusto: z.boolean(),
  ativo: z.boolean(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type TipoMovimentacaoEstoque = z.infer<typeof tipoMovimentacaoEstoqueSchema>;

export const listaTiposMovimentacaoEstoqueSchema = respostaListaSchema(
  tipoMovimentacaoEstoqueSchema,
);
export type ListaTiposMovimentacaoEstoque = z.infer<typeof listaTiposMovimentacaoEstoqueSchema>;

/**
 * Listagem, com um filtro além dos comuns.
 *
 * `sentido` porque a tela de lançamento de estoque já sabe para que lado o
 * material vai, e oferecer os tipos de saída em um lançamento de entrada é a
 * forma mais direta de o usuário gravar o movimento invertido. O campo de busca
 * não resolve: "devolução" existe nos dois sentidos.
 */
export const listarTiposMovimentacaoEstoqueQuerySchema = listagemDeCadastroSchema.extend({
  sentido: sentidoMovimentacaoEstoqueSchema.optional(),
});

export type ListarTiposMovimentacaoEstoqueQuery = z.infer<
  typeof listarTiposMovimentacaoEstoqueQuerySchema
>;

/**
 * Colunas de ordenação da listagem.
 *
 * A ordem padrão é `nome`, crescente: o cadastro é curto e se procura pelo nome.
 * `sentido` entra porque "quais tipos são de saída?" é a segunda pergunta que se
 * faz a esta tela.
 */
export const COLUNAS_ORDENAVEIS_TIPO_MOVIMENTACAO_ESTOQUE = [
  'nome',
  'sentido',
  'createdAt',
] as const;
